import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/Card';
import { apiFetch, parseJsonResponse } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

interface MySQLStats {
  connections: {
    current: number;
    max: number;
    percentage: number;
    status: 'ok' | 'warning' | 'critical';
  };
  queries: {
    total: number;
    slow: number;
  };
}

interface LogStats {
  mysql: {
    total_lines: number;
    file_size: number;
    warnings: number;
    errors: number;
  };
  product_sync: {
    total_lines: number;
    file_size: number;
    warnings: number;
    errors: number;
  };
  screen_access: {
    total_lines: number;
    file_size: number;
    warnings: number;
    errors: number;
  };
  cache: {
    total_lines: number;
    file_size: number;
    warnings: number;
    errors: number;
  };
  errors: {
    total_lines: number;
    file_size: number;
    warnings: number;
    errors: number;
  };
}

interface ScreenStats {
  active_last_24h: number;
  total_accesses_last_24h: number;
}

interface SyncStats {
  total_syncs_today: number;
  total_products_synced: number;
  average_duration: number;
}

interface MonitoringStats {
  mysql: MySQLStats;
  logs: LogStats;
  screens: ScreenStats;
  sync: SyncStats;
  timestamp: string;
}

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
}

export const Monitoring: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<MonitoringStats | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [selectedLogType, setSelectedLogType] = useState<string>('mysql');
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Función helper para convertir error a string de forma segura
  const getErrorMessage = (error: any, defaultMessage: string): string => {
    if (typeof error === 'string') {
      return error;
    }
    if (typeof error === 'boolean' && error) {
      return defaultMessage;
    }
    return defaultMessage;
  };

  const fetchStats = useCallback(async () => {
    if (!token) {
      setError('No hay token de autenticación');
      setLoading(false);
      return;
    }
    
    try {
      setError(null);
      const res = await apiFetch('monitoring/stats', {}, token);
      const json = await parseJsonResponse(res);
      
      console.log('📊 [Monitoring] Respuesta recibida:', {
        ok: res.ok,
        status: res.status,
        hasData: !!json.data,
        hasError: !!json.error,
        json: json
      });
      
      // Verificar si hay error en la respuesta
      if (json.error || !res.ok) {
        const errorMessage = json.message || getErrorMessage(json.error, 'Error al cargar estadísticas');
        console.error('❌ [Monitoring] Error en respuesta:', errorMessage);
        setError(errorMessage);
        setStats(null);
        return;
      }
      
      // Verificar que los datos estén completos y tengan la estructura esperada
      if (json.data) {
        // Validar estructura básica de datos
        if (json.data.mysql && json.data.logs && json.data.screens && json.data.sync) {
          console.log('✅ [Monitoring] Datos recibidos correctamente');
          setStats(json.data);
          setError(null);
        } else {
          console.warn('⚠️ [Monitoring] Estructura de datos incompleta:', json.data);
          setError('Los datos recibidos están incompletos');
          setStats(null);
        }
      } else {
        console.warn('⚠️ [Monitoring] Respuesta sin datos:', json);
        setError('No se recibieron datos del servidor');
        setStats(null);
      }
    } catch (error: any) {
      console.error('❌ [Monitoring] Error cargando estadísticas:', error);
      setError(error.message || 'Error de conexión al cargar estadísticas');
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchLogs = useCallback(async () => {
    if (!token) return;
    
    setLogsLoading(true);
    try {
      const res = await apiFetch(`monitoring/logs?type=${selectedLogType}&lines=50`, {}, token);
      const json = await parseJsonResponse(res);
      
      console.log('📋 [Monitoring] Logs recibidos:', {
        ok: res.ok,
        status: res.status,
        hasData: !!json.data,
        hasLogs: !!(json.data && json.data.logs),
        logCount: json.data?.logs?.length || 0
      });
      
      if (json.data && !json.error && json.data.logs) {
        // Parsear logs para extraer timestamp, level y message
        const parsedLogs = json.data.logs.map((logLine: string) => {
          const match = logLine.match(/\[([^\]]+)\]\s+\[([^\]]+)\]\s+(.+)/);
          if (match) {
            return {
              timestamp: match[1],
              level: match[2],
              message: match[3]
            };
          }
          return {
            timestamp: '',
            level: 'INFO',
            message: logLine
          };
        });
        setLogs(parsedLogs);
      } else if (json.error) {
        console.warn('⚠️ [Monitoring] Error al cargar logs:', json.error);
        setLogs([]);
      } else {
        console.log('ℹ️ [Monitoring] No hay logs disponibles');
        setLogs([]);
      }
    } catch (error: any) {
      console.error('❌ [Monitoring] Error cargando logs:', error);
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [selectedLogType, token]);

  useEffect(() => {
    fetchStats();
    
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchStats();
      }, 30000); // Actualizar cada 30 segundos
      
      return () => clearInterval(interval);
    }
  }, [fetchStats, autoRefresh]);

  useEffect(() => {
    if (selectedLogType) {
      fetchLogs();
    }
  }, [selectedLogType, fetchLogs]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'critical':
        return 'bg-red-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'ok':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'critical':
        return 'Crítico';
      case 'warning':
        return 'Advertencia';
      case 'ok':
        return 'Normal';
      default:
        return 'Desconocido';
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'ERROR':
        return 'text-red-600 bg-red-50';
      case 'WARNING':
        return 'text-yellow-600 bg-yellow-50';
      case 'INFO':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <DashboardLayout 
        title="Monitoreo del Sistema"
        subtitle="Estadísticas y logs en tiempo real"
        showBackButton={true}
        backPath="/management"
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Cargando estadísticas...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="Monitoreo del Sistema"
      subtitle="Estadísticas y logs en tiempo real"
      showBackButton={true}
      backPath="/management"
    >
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className="text-xs sm:text-sm text-gray-700">Auto-actualizar (30s)</span>
            </label>
            <button
              onClick={fetchStats}
              className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-auto"
            >
              Actualizar
            </button>
          </div>
        </div>

        {/* Mensaje de error */}
        {error && !loading && (
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-red-800">Error al cargar estadísticas</p>
                  <p className="text-xs text-red-600 mt-1 break-words">{error}</p>
                </div>
                <button
                  onClick={fetchStats}
                  className="px-3 py-1.5 text-xs sm:text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors w-full sm:w-auto"
                >
                  Reintentar
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mensaje cuando no hay datos pero tampoco hay error */}
        {!stats && !loading && !error && (
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="text-center py-6 sm:py-8">
                <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p className="text-sm sm:text-base text-gray-500 mb-2">No hay datos disponibles</p>
                <p className="text-xs sm:text-sm text-gray-400 mb-4 px-2">Las estadísticas aparecerán aquí una vez que haya actividad en el sistema</p>
                <button
                  onClick={fetchStats}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Actualizar
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {stats && (
          <>
            {/* MySQL Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <Card>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm text-gray-600">Conexiones MySQL</p>
                      <p className="text-xl sm:text-2xl font-bold mt-1">
                        {stats.mysql.connections.current} / {stats.mysql.connections.max}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-1">
                        {stats.mysql.connections.percentage}% utilizado
                      </p>
                    </div>
                    <div className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0 ${getStatusColor(stats.mysql.connections.status)}`} 
                         title={getStatusText(stats.mysql.connections.status)} />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-3 sm:p-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Queries Totales</p>
                    <p className="text-xl sm:text-2xl font-bold mt-1">
                      {stats.mysql.queries.total.toLocaleString()}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">
                      {stats.mysql.queries.slow} queries lentas
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-3 sm:p-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Pantallas Activas (24h)</p>
                    <p className="text-xl sm:text-2xl font-bold mt-1">
                      {stats.screens.active_last_24h}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">
                      {stats.screens.total_accesses_last_24h.toLocaleString()} accesos
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-3 sm:p-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Sincronizaciones Hoy</p>
                    <p className="text-xl sm:text-2xl font-bold mt-1">
                      {stats.sync.total_syncs_today}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">
                      {stats.sync.total_products_synced.toLocaleString()} productos
                    </p>
                    {stats.sync.average_duration > 0 && (
                      <p className="text-xs text-gray-400 mt-1">
                        Promedio: {stats.sync.average_duration}s
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Logs Stats */}
            <Card>
              <CardContent className="p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">Estadísticas de Logs (Hoy)</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4">
                  {Object.entries(stats.logs).map(([type, data]) => (
                    <div key={type} className="border rounded-lg p-2 sm:p-3">
                      <p className="text-xs sm:text-sm font-semibold text-gray-700 capitalize truncate">
                        {type.replace('_', ' ')}
                      </p>
                      <p className="text-base sm:text-lg font-bold mt-1">{data.total_lines}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatBytes(data.file_size)}
                      </p>
                      {data.errors > 0 && (
                        <p className="text-xs text-red-600 mt-1">
                          {data.errors} errores
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Logs Viewer */}
            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4">
                  <h2 className="text-lg sm:text-xl font-bold">Visor de Logs</h2>
                  <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                    {['mysql', 'product_sync', 'screen_access', 'cache', 'errors'].map((type) => (
                      <button
                        key={type}
                        onClick={() => setSelectedLogType(type)}
                        className={`px-2 sm:px-3 py-1 rounded text-xs sm:text-sm transition-colors flex-1 sm:flex-none ${
                          selectedLogType === type
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {type.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                {logsLoading ? (
                  <div className="text-center py-8 text-gray-500 text-sm">Cargando logs...</div>
                ) : logs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">No hay logs disponibles</div>
                ) : (
                  <div className="bg-gray-900 text-gray-100 rounded-lg p-3 sm:p-4 font-mono text-xs sm:text-sm max-h-64 sm:max-h-96 overflow-y-auto">
                    {logs.map((log, index) => (
                      <div key={index} className="mb-2 break-words">
                        <span className="text-gray-500">[{log.timestamp}]</span>
                        <span className={`ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 rounded text-xs ${getLevelColor(log.level)}`}>
                          {log.level}
                        </span>
                        <span className="ml-1 sm:ml-2 break-all">{log.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Last Update */}
            <div className="text-center text-xs sm:text-sm text-gray-500 px-2">
              Última actualización: {new Date(stats.timestamp).toLocaleString('es-ES')}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

